'use client';

import type { ReactNode } from 'react';

import { cn } from '../../utils/cn';
import { SpinnerBorder } from '../spinner-border';

export type ComposerTone = 'default' | 'private' | 'recording';

interface ComposerShellProps {
	children: ReactNode;
	tone?: ComposerTone;
	pending?: boolean;
	/** When true, uses a more translucent surface (50%) so background art shows through — e.g. the landing hero canvas. */
	translucent?: boolean;
	/**
	 * Corner radius. `'lg'` (16px, default) is the card-like expanded shape;
	 * `'xl'` (24px) is the rounder resting/default composer. The mobile morph
	 * surface mirrors these (resting 24 ⇄ expanded 16).
	 */
	radius?: 'lg' | 'xl';
	className?: string;
}

/**
 * The shared composer surface recipe — border + blur + the resting `bg-surface-80`
 * fill + the color transition. Shadow is intentionally NOT included: each consumer
 * adds its own (`ComposerShell` → `shadow-floating`; the morph `ComposerSurface` →
 * `shadow-lifted`), since those are custom utilities that don't reliably dedupe via
 * tailwind-merge. Exported so the morph surface paints the SAME single surface
 * instead of duplicating it. (`translucent` callers override the bg to `bg-surface-25`.)
 */
export const composerSurfaceBaseClass =
	'relative flex w-full flex-col rounded-[16px] border backdrop-blur-xl bg-surface-80 transition-colors duration-200 ease-out';

// Idle border is transparent so the shadow alone defines the resting shape;
// on hover the border-color fades in to 10% alpha (a faint hint), and on
// focus-within it strengthens to 25% so the shell lights up while the editor
// (or any descendant) has focus. Hover is gated on `not-focus-within` so the
// focused state unconditionally wins when both apply. The 1px is always
// reserved so there's no layout jump on the transition.
//
// Private/recording tones paint an additional translucent theme tint over the
// shell bg via `background-image`. The tint uses hex-with-alpha literals
// rather than color-mix() because Safari 16.2–16.3 silently drops color-mix()
// when it appears inside linear-gradient().
export const composerToneClasses: Record<ComposerTone, string> = {
	default: 'border-transparent not-focus-within:hover:border-neutral/10 focus-within:border-neutral/25',
	private: cn(
		// 5% --color-private (#ff6e34) — theme-invariant, hardcoded literal.
		'bg-[image:linear-gradient(#ff6e340d,#ff6e340d)]',
		'border-transparent not-focus-within:hover:border-private/10 focus-within:border-private/25',
	),
	recording: cn(
		// 5% --color-danger (#ef5e61) — theme-invariant, hardcoded literal.
		'bg-[image:linear-gradient(#ef5e610d,#ef5e610d)]',
		'border-transparent not-focus-within:hover:border-danger/10 focus-within:border-danger/25',
	),
};

export const ComposerShell = ({
	children,
	tone = 'default',
	pending = false,
	translucent = false,
	radius = 'lg',
	className,
}: ComposerShellProps) => {
	// The radius is also published as `--composer-radius` so nested seats (the
	// welcome hero mic) can derive a concentric corner from whatever shape the
	// hosting shell has.
	const roundedClass =
		radius === 'xl'
			? 'rounded-[24px] [--composer-radius:24px]'
			: 'rounded-[16px] [--composer-radius:16px]';
	const card = (
		<div
			className={cn(
				composerSurfaceBaseClass,
				'shadow-floating',
				roundedClass,
				translucent && 'bg-surface-25',
				composerToneClasses[tone],
				className,
			)}
			data-tone={tone}
		>
			{children}
		</div>
	);

	// Toggle the spinner border via `active` instead of conditionally wrapping:
	// swapping the tree root on `pending` would remount `card` (and recreate the
	// Tiptap editor) on every send. The wrapper stays mounted at all times; when
	// not pending the border is parked and invisible (zero cost). Mirrors
	// `ComposerSurface`, which depends on this stability to survive an audio send.
	return (
		<SpinnerBorder active={pending} className={cn('w-full', roundedClass)}>
			{card}
		</SpinnerBorder>
	);
};
